import { useState } from 'react'
import { Plus, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { useIdentity } from '@/features/permissions/AbilityProvider'
import { crm2Api, useCrm2Meta, useCrm2Write } from './api'
import { FormGroup, MainAccountSelect, Readiness, SizeSelect, Suggestions } from './accountFields'
import { Field, fieldClass, PersonSelect, PHONE_HINT, PHONE_PLACEHOLDER, ROLE_LABEL } from './shared'

type Person = { name: string; title: string; email: string; phone: string; role: string }
const blank: Person = { name: '', title: '', email: '', phone: '', role: 'sponsor' }

/** A company on its own, before any deal: who owns it, what kind of company it is, its first people, and where it sits. */
export function NewAccountDialog({ onClose, onCreated, parentId: presetParent }: { onClose: () => void; onCreated: (id: string) => void; parentId?: string }) {
  const me = useIdentity()
  const roles = useCrm2Meta().data?.contactRoles ?? []
  const [f, setF] = useState({ name: '', domain: '', industry: '', size: '', ownerId: me.userId as string | null, source: '', parentId: presetParent ?? '', notes: '' })
  const [people, setPeople] = useState<Person[]>([{ ...blank }])
  const create = useCrm2Write(
    () =>
      crm2Api.createAccount({
        name: f.name.trim(),
        domain: f.domain.trim() || null,
        industry: f.industry.trim() || null,
        size: f.size || null,
        ownerId: f.ownerId,
        source: f.source.trim() || null,
        notes: f.notes.trim() || null,
        parentId: f.parentId || null,
        contacts: people.filter((p) => p.name.trim()).map((p) => ({ name: p.name.trim(), title: p.title.trim() || null, email: p.email.trim() || null, phone: p.phone.trim() || null, role: p.role })),
      }),
    (a) => `${a.name} added`,
  )
  const setP = (i: number, patch: Partial<Person>) => setPeople(people.map((p, j) => (j === i ? { ...p, ...patch } : p)))
  const first = people[0] ?? blank

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle>New account</DialogTitle>
          <DialogDescription>A company before there is a deal. Add the deal from its page when there is one.</DialogDescription>
        </DialogHeader>
        <Suggestions />
        <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_15rem]">
          <div className="grid gap-4">
            <FormGroup title="Company" fills="the header and Account properties">
              <Field label="Company name">
                <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Acme Staffing" />
              </Field>
              <Field label="Website or domain" hint="Matches emails and future deals to this account.">
                <Input value={f.domain} onChange={(e) => setF({ ...f, domain: e.target.value })} placeholder="acme.com" />
              </Field>
              <Field label="Industry">
                <Input list="crm2-industries" value={f.industry} onChange={(e) => setF({ ...f, industry: e.target.value })} placeholder="BPO / Contact center" />
              </Field>
              <Field label="Size">
                <SizeSelect value={f.size} onChange={(v) => setF({ ...f, size: v })} />
              </Field>
              <Field label="Main account" hint="For a region or department of a bigger company.">
                <MainAccountSelect value={f.parentId} onChange={(v) => setF({ ...f, parentId: v })} />
              </Field>
            </FormGroup>

            <FormGroup title="Ownership" fills="Account highlights">
              <Field label="Account owner">
                <PersonSelect label="Account owner" value={f.ownerId} onChange={(v) => setF({ ...f, ownerId: v })} />
              </Field>
              <Field label="Lead source">
                <Input list="crm2-sources" value={f.source} onChange={(e) => setF({ ...f, source: e.target.value })} placeholder="Referral" />
              </Field>
              <div className="sm:col-span-2">
                <Field label="Description and notes">
                  <Textarea rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} placeholder="What they do, who we know there, anything worth remembering" />
                </Field>
              </div>
            </FormGroup>

            <FormGroup title="People" fills="Contacts, each with their own page">
              <div className="grid gap-3 sm:col-span-2">
                {people.map((p, i) => (
                  <div key={i} className="grid gap-2 sm:grid-cols-[1fr_1fr_1fr_8rem_auto]">
                    <Input aria-label="Name" placeholder="Name" value={p.name} onChange={(e) => setP(i, { name: e.target.value })} />
                    <Input aria-label="Title" placeholder="Title" value={p.title} onChange={(e) => setP(i, { title: e.target.value })} />
                    <Input aria-label="Email" type="email" placeholder="Email" value={p.email} onChange={(e) => setP(i, { email: e.target.value })} />
                    <select aria-label="Role" value={p.role} onChange={(e) => setP(i, { role: e.target.value })} className={fieldClass}>
                      {roles.map((r) => (
                        <option key={r} value={r}>
                          {ROLE_LABEL[r] ?? r}
                        </option>
                      ))}
                    </select>
                    <Button type="button" size="icon" variant="ghost" aria-label="Remove this person" disabled={people.length === 1} onClick={() => setPeople(people.filter((_, j) => j !== i))}>
                      <X className="size-4" />
                    </Button>
                    <Input aria-label="Contact number" type="tel" placeholder={`Contact number, e.g. ${PHONE_PLACEHOLDER}`} title={PHONE_HINT} value={p.phone} onChange={(e) => setP(i, { phone: e.target.value })} className="sm:col-span-2" />
                  </div>
                ))}
                {people.length < 5 ? (
                  <Button type="button" size="sm" variant="outline" className="w-fit" onClick={() => setPeople([...people, { ...blank, role: 'user' }])}>
                    <Plus className="size-4" />
                    Another person
                  </Button>
                ) : null}
              </div>
            </FormGroup>
          </div>
          <Readiness
            items={[
              { label: 'Company name', done: Boolean(f.name.trim()), required: true },
              { label: 'Domain', done: Boolean(f.domain.trim() || first.email.includes('@')) },
              { label: 'Industry', done: Boolean(f.industry.trim()) },
              { label: 'Size', done: Boolean(f.size) },
              { label: 'Account owner', done: Boolean(f.ownerId) },
              { label: 'Lead source', done: Boolean(f.source.trim()) },
              { label: 'A contact with title and email', done: Boolean(first.name.trim() && first.title.trim() && first.email.trim()) },
              { label: 'Contact number (for WhatsApp)', done: Boolean(first.phone.trim()) },
              { label: 'Main account (if a sub-account)', done: Boolean(f.parentId) },
            ]}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => create.mutate(undefined, { onSuccess: (a) => { onClose(); onCreated(a.id) } })} disabled={create.isPending || !f.name.trim()}>
            {create.isPending ? 'Adding…' : 'Add account'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
